/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ProgressCardStyles
 *
 * @file ProgressCardStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CustomCard from '../../common/CustomCard';
import mixins from '../../../assets/styles/mixins.scss';

export const ProgressCardRoot = styled(CustomCard)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: '16px'
  }
});

export const ProgressCardContent = styled('div')({
  '&&': {
    display: 'flex',
    gap: 16
  }
});

export const ProgressCardHeader = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    height: 24,
    width: '100%',
    justifyContent: 'space-between'
  }
});

export const ProgressCardTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    fontWeight: mixins.fontWeight500,
    flex: 1,
    letterSpacing: '-0.4'
  }
});

export const ProgressCardBody = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start',
    width: '100%'
  }
});

export const ProgressStatsSection = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    height: 128,
    width: '100%'
  }
});

export const ProgressItemsList = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    flex: 1
  }
});

export const ProgressItem = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    width: 'fit-content',
    cursor: 'pointer'
  }
});

export const StatusDotContainer = styled('div')({
  '&&': {
    width: 8,
    height: 8,
    position: 'relative'
  }
});

export const DotBase = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ isActive }) => ({
  '&&': {
    borderRadius: '50%',
    width: 8,
    height: 8,
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: 0,
    bottom: 0,
    ...(isActive ? {
      outline: '1px solid currentColor',
      outlineOffset: 1
    } : {})
  }
}));

export const StatusLabel = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: 1.25
  }
});

export const PercentageText = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight600,
    marginLeft: 4,
    lineHeight: 1.25
  }
});

export const ProgressCardFooter = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  }
});

export const DateSection = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center'
  }
});

export const DateLabel = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500
  }
});

export const BadgeWrapper = styled('div')({
  '&&': {
    padding: '2px 0px'
  }
});
